type ConversationFeedTab = "connectors" | "custom" | "tasks";
type ConversationQueryTab = "integrations" | "custom" | "tasks" | "agents";

// THE INBOX item lanes (inbox-is-work wave) — kind → the ref set that
// classifies a live ws frame onto that inbox lane. The kinds are exactly the
// inbox row's declared kinds (sci//inbox:kind:@{notify,status}). The
// writer is world control/feed.py status_target_of: the DECLARED status faces
// (STATUS_FACE_ATOMS) + the initiating sci::human:* ref, with the
// notify lane taking precedence by its OWN atom.
// THE MACHINE CONVENTION (Ori's oracle ruling, 2026-07-25): every kind carries
// the SAME two faces — <kind>:@machine:@binary is the lifecycle IDENTITY (it
// exists) and <kind>:status:@status is the status WORD; the fused
// <kind>:@machine:@status spelling is GONE from the oracle. So each status
// lane here is that kind's status SLOT, never its lifecycle binary:
// classifying a binary as a status folded a live entry the world never
// accreted — reload lost it (the split carrier, closed 2026-07-25).
// sci//inbox:kind:@status rides ONLY the inbox ROW's refs, never a live
// source frame. Mirrored in mcp-outlet mcpServerTools.ts INBOX_ITEM_REF_SETS —
// keep both in sync. The client uses them ONLY inside the ONE store fold
// (feedStore.foldLiveAnnouncement); the served inbox list comes from
// `query {tab:"feed"}`.
export const INBOX_ITEM_REF_SETS = {
  notify: ["sci//notify:@machine:@binary"],
  status: [
    "sci//goal:status:@status",
    "sci//activity:status:@status",
    "sci//artifact:status:@status",
    "sci//instance:status:@status",
    "sci//integration:status:@status",
  ],
} as const;

// THE FEED/INBOX row book lanes — a live ws frame carrying the human's OWN
// refreshed feed/inbox row (the ws-refresh face of the `query {tab:"feed"}`
// bodies). Mirrored in mcp-outlet mcpServerTools.ts FEED_ROW_REF /
// INBOX_ROW_REF — keep both in sync.
export const FEED_ROW_REF = "sci//@human:@feed:@machine" as const;
export const INBOX_ROW_REF = "sci//@human:@inbox:@machine" as const;

// The template family (sci//template:@machine:@binary) — a served rail item
// declaring it is a registered template card (renders the Fork affordance,
// write lane sci//artifact:activate:@activated). Mirrored in mcp-outlet
// mcpServerTools.ts TEMPLATE_FAMILY_REFS — keep both in sync.
export const TEMPLATE_FAMILY_REFS = ["sci//template:@machine:@binary"] as const;

// Workspace = the conversations the HUMAN created — the explicit oracle origin
// marker, stamped by world at the creating control path. The composed VALUE
// is built from the DECLARED family atom + the actor reference (the same
// composition the world's ONE composer conversation_creator stamps on the
// wire); the code speaks only the declared family spelling. NEVER inferred
// from sender:@human announcements (gate input rows carry that declared
// sender while their body sender is gate); connector surfaces and
// notifications live on their own asks.
const conversationCreatorOf = (kind: "human" | "agent"): string =>
  "sci//conversation:creator:" + `@(@actor:@${kind}:@machine)`;
export const WORKSPACE_REFS = [
  conversationCreatorOf("human"),
] as const;

// The AGENT's own conversations (control:@conversation:@creator @agent) — the
// second half of the human's workspace, a SEPARATE ref-set (OR), never folded
// into the human creator set (a conversation carries exactly ONE creator).
// THE WORKSPACE IS HUMAN+AGENT ONLY (Ori 2026-07-25): the gate-scored statement
// reports, the artifact boxes, and the harness are real actors with real
// conversations — they are NOT this human's workspace. Mirrored by the served
// `workspace` tab selector (world control/feed.py) and by mcp-outlet
// WORKSPACE_CONVERSATION_REF_SETS — keep all three in sync.
export const AGENT_WORKSPACE_REFS = [
  conversationCreatorOf("agent"),
] as const;

// Tasks = the request/feedback queue rows (kind=task, the oracle task family
// sci//task:@machine:@binary). The outlet owns the scope split — the org's own
// queue (sci::org:<org>) plus the PUBLIC sci queue (root, visible to all).
// Mirrored in mcp-outlet mcpServerTools.ts TASK_QUEUE_REFS — keep both in sync.
export const TASK_QUEUE_REFS = [
  "sci//task:@machine:@binary",
] as const;

// The agent BOOK lanes (agent wave) — mirrored in mcp-outlet mcpServerTools.ts
// (AGENT_ORG_LANE_REF / AGENT_HUMAN_LANE_REF) — keep both in sync. The outlet
// owns the scope split for `query {tab:"agents"}`: the org's agents by the org
// lane, the bearer's PERSONAL agents by the human lane, and the ROOT book (the
// platform agent — scopeless, read by the machine family at sci//). A served
// agent row declaring NEITHER lane is a root/platform agent.
export const AGENT_ORG_LANE_REF = "sci//@org:agent:@agent**" as const;
export const AGENT_HUMAN_LANE_REF = "sci//@human:agent:@agent**" as const;

// Connectors = ARTIFACT-sent surface announcements (announcement:sender:@artifact
// + the kind's database atom). The sender atom is the boundary discriminator —
// gate-sent integration/data announcements never match this ask.
const SURFACE_SENDER = "sci//announcement:sender:@(@actor:@artifact:@machine)";

// Client-facing connector filters. The historical label remains "entity" for
// the picker UI, but its source is the provider's workflow-backed SURFACE
// database. A legal Entity exists only after the Financial template is forked;
// connector discovery must never depend on or mint that Book role.
export const CONNECTOR_SURFACE_REF_SETS = {
  entity: [SURFACE_SENDER, "sci//instance:@database:@surface**"],
  tool: [SURFACE_SENDER, "sci//instance:@database:@tool**"],
  channel: [SURFACE_SENDER, "sci//instance:@database:@channel**"],
} as const;

export const queryRefsForFeed = (
  tab: ConversationFeedTab | ConversationQueryTab,
  connectorFilter: keyof typeof CONNECTOR_SURFACE_REF_SETS | "all" = "all",
): string[] | string[][] => {
  if (tab === "tasks") return [[...TASK_QUEUE_REFS]];
  // workspace = the human's OWN conversations OR the agent's (OR-sets)
  if (tab === "custom") return [[...WORKSPACE_REFS], [...AGENT_WORKSPACE_REFS]];
  if (tab === "connectors" || tab === "integrations") {
    if (connectorFilter !== "all") {
      return [[...CONNECTOR_SURFACE_REF_SETS[connectorFilter]]];
    }
    return Object.values(CONNECTOR_SURFACE_REF_SETS).map((refs) => [...refs]);
  }
  return [];
};

export const queryKindForFeed = (tab: ConversationFeedTab | ConversationQueryTab): string | undefined => {
  if (tab === "custom") return "conversation"; // workspace = conversation rows by creator
  if (tab === "tasks") return "task"; // the queue = canonical task rows
  return undefined;
};
